import type { z } from 'zod';
import type { Session } from '../auth/session';
import { ApiError, NetworkError, toApiError } from './errors';
import { apiUrl } from './origin';

export interface RequestOptions<S extends z.ZodTypeAny | undefined> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Validates the response at the boundary. Contracts are Zod schemas. */
  schema?: S;
  signal?: AbortSignal;
}

type Output<S> = S extends z.ZodTypeAny ? z.output<S> : unknown;

export interface ApiClient {
  request<S extends z.ZodTypeAny | undefined = undefined>(
    path: string,
    options?: RequestOptions<S>,
  ): Promise<Output<S>>;
}

/**
 * Authenticated fetch. Attaches the in-memory access token and, on a 401,
 * refreshes once and retries once — a second 401 means the session is over.
 */
export function createApiClient(session: Session, doFetch: typeof fetch = fetch): ApiClient {
  async function send(path: string, options: RequestOptions<z.ZodTypeAny | undefined>) {
    const token = await session.accessToken();
    const headers: Record<string, string> = {};
    if (token) headers['authorization'] = `Bearer ${token}`;
    if (options.body !== undefined) headers['content-type'] = 'application/json';

    try {
      return await doFetch(apiUrl(path), {
        method: options.method ?? 'GET',
        headers,
        credentials: 'same-origin',
        ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
      });
    } catch (cause) {
      throw new NetworkError(cause);
    }
  }

  async function request<S extends z.ZodTypeAny | undefined = undefined>(
    path: string,
    options: RequestOptions<S> = {},
  ): Promise<Output<S>> {
    let response = await send(path, options);
    if (response.status === 401 && (await session.refresh()) !== null) {
      response = await send(path, options);
    }
    if (!response.ok) throw await toApiError(response);
    if (response.status === 204) return undefined as Output<S>;

    const json: unknown = await response.json();
    return (options.schema ? options.schema.parse(json) : json) as Output<S>;
  }

  return { request };
}

export { ApiError, NetworkError };
